import { getApiBaseUrl } from "../config/runtimeConfig";
import { invalidateLookup } from "../lookups/lookupsApi";

const apiBaseUrl = getApiBaseUrl();

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export interface ArticleCategory {
  id: number;
  name: string;
  code?: string | null;
  created: string;
  lastModified: string;
}

export interface ArticleCategoryPayload {
  name: string;
  code?: string | null;
}

export interface Article {
  id: number;
  code: string;
  name: string;
  unit?: string | null;
  articleCategoryId?: number | null;
  categoryName?: string | null;
  referencePrice?: number | null;
  classificationId?: number | null;
  suggestedClassificationCode?: string | null;
  created: string;
  lastModified: string;
}

export interface ArticlePayload {
  code: string;
  name: string;
  unit?: string | null;
  articleCategoryId?: number | null;
  classificationId?: number | null;
  referencePrice?: number | null;
}

export interface ArticleClassification {
  id: number;
  code: string;
  name: string;
  sortOrder: number;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }

  return (await response.json()) as T;
}

async function requestVoid(path: string, options?: RequestInit): Promise<void> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }
}

// Article categories

export function getArticleCategories(): Promise<ArticleCategory[]> {
  return request<ArticleCategory[]>("/api/articlecategories");
}

export function createArticleCategory(
  payload: ArticleCategoryPayload,
): Promise<ArticleCategory> {
  return request<ArticleCategory>("/api/articlecategories", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function updateArticleCategory(
  categoryId: number,
  payload: ArticleCategoryPayload,
): Promise<ArticleCategory> {
  return request<ArticleCategory>(`/api/articlecategories/${categoryId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function deleteArticleCategory(categoryId: number): Promise<void> {
  return requestVoid(`/api/articlecategories/${categoryId}`, {
    method: "DELETE",
  });
}

// Articles

export function getArticles(): Promise<Article[]> {
  return request<Article[]>("/api/articles");
}

export function getArticleById(articleId: number): Promise<Article> {
  return request<Article>(`/api/articles/${articleId}`);
}

export function createArticle(payload: ArticlePayload): Promise<Article> {
  return request<Article>("/api/articles", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }).then((result) => {
    invalidateLookup("articles");
    return result;
  });
}

export function updateArticle(
  articleId: number,
  payload: ArticlePayload,
): Promise<Article> {
  return request<Article>(`/api/articles/${articleId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }).then((result) => {
    invalidateLookup("articles");
    return result;
  });
}

export function deleteArticle(articleId: number): Promise<void> {
  return requestVoid(`/api/articles/${articleId}`, {
    method: "DELETE",
  }).then((result) => {
    invalidateLookup("articles");
    return result;
  });
}

// Article classifications (reference list)

export function getArticleClassifications(): Promise<ArticleClassification[]> {
  return request<ArticleClassification[]>("/api/articles/classifications");
}
